import React from "react";
import { InfoTile } from "./LeadInfoTiles";
import { labelize } from "@/lib/constants";

/** All PI case-detail fields the platform tracks. `qualified` marks the
 * fields whose value is a hard gate at ingest time. */
const CASE_FIELDS = [
  { name: "accident_type", label: "Accident type", format: "labelize", qualified: true },
  { name: "incident_date", label: "Incident date", format: "date", qualified: true },
  { name: "at_fault", label: "At fault", format: "yesno", qualified: true, expected: false },
  { name: "currently_represented", label: "Currently represented", format: "yesno", qualified: true, expected: false },
  { name: "physically_injured", label: "Physically injured", format: "yesno", qualified: true, expected: true },
  { name: "medical_treatment", label: "Medical treatment", format: "yesno", qualified: true, expected: true },
  { name: "phone_verified", label: "Phone verified", format: "yesno" },
  { name: "injuries", label: "Injuries" },
  { name: "accident_description", label: "Case description" },
  { name: "location", label: "Accident location" },
  { name: "report_number", label: "Report number" },
  { name: "vin", label: "VIN" },
  { name: "driver_license", label: "Driver license" },
  { name: "ip_address", label: "IP address" },
];

function formatValue(spec, raw) {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (spec.format === "yesno") {
    if (raw === true) return "Yes";
    if (raw === false) return "No";
    return String(raw);
  }
  if (spec.format === "labelize") return labelize(raw);
  if (spec.format === "date") {
    try {
      return new Date(raw).toLocaleDateString();
    } catch {
      return String(raw);
    }
  }
  return String(raw);
}

function pillClass(spec, raw) {
  if (!spec.qualified || raw === null || raw === undefined || raw === "") return "";
  if (spec.format !== "yesno") return "";
  if (raw === spec.expected) return "text-emerald-700 font-bold";
  return "text-rose-700 font-bold";
}

function RejectionBanner({ reasons }) {
  if (!reasons || !reasons.length) return null;
  return (
    <div className="border border-rose-600 bg-rose-50 p-3 mb-4" data-testid="case-details-rejection">
      <div className="text-[10px] uppercase tracking-widest font-bold text-rose-800">
        Rejected — {reasons.length} PI gate{reasons.length > 1 ? "s" : ""} failed
      </div>
      <ul className="mt-1 font-mono text-[11px] space-y-1 text-rose-800">
        {reasons.map((r) => (
          <li key={r.code}>
            <span className="font-bold">{r.gate}</span>: {r.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Case Details card for the LeadDetail Overview tab.
 *
 * Renders every field the affiliate/partner might send for a personal-injury
 * lead. The four "qualifier" fields (at_fault / currently_represented /
 * physically_injured / medical_treatment) get colour-coded so a reviewer
 * can spot bad quality at a glance.
 *
 * If the lead was rejected at intake (status="rejected_invalid"), the
 * per-gate reasons are surfaced at the top of the card.
 */
export default function LeadCaseDetails({ lead }) {
  if (!lead) return null;
  const hasAny = CASE_FIELDS.some((f) => {
    const v = lead[f.name];
    return v !== null && v !== undefined && v !== "";
  });
  if (!hasAny && !lead.rejection_reasons?.length) return null;

  return (
    <div className="brut-card p-5 lg:col-span-3 mt-4" data-testid="lead-case-details">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-chivo font-bold uppercase tracking-tight">Case details</div>
        <div className="label-mono">personal injury qualifiers</div>
      </div>
      <RejectionBanner reasons={lead.rejection_reasons} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm font-mono">
        {CASE_FIELDS.map((spec) => {
          const raw = lead[spec.name];
          return (
            <InfoTile
              key={spec.name}
              k={spec.label}
              v={<span className={pillClass(spec, raw)} data-testid={`case-${spec.name}`}>{formatValue(spec, raw)}</span>}
            />
          );
        })}
      </div>
    </div>
  );
}
